"use client";

import { cloneElement, type ReactElement, type ReactNode } from "react";
import Link from "next/link";
import type { CatalogShelf } from "@/lib/api/api-client";
import {
  libraryBrowseAllPath,
  libraryHomePath,
  libraryPlaylistPath,
  librarySelectionPath,
  LIBRARY_MINE_PATH,
  LIBRARY_PLAYLISTS_PATH,
  LIBRARY_RESUME_PATH,
  LIBRARY_SHARED_PATH,
  type LibraryBrowseState,
} from "@/lib/library/library-routes";
import { useLibraryOverview } from "./library-overview";

/**
 * La carte du répertoire, à gauche.
 *
 * Reprend le gabarit de sections de `harmony-atlas.tsx` (`Section` + `Row`),
 * avec une différence de fond : ses lignes sont des **liens** et non des
 * boutons, parce qu'elles mènent à des URL. On récupère gratuitement le clic
 * milieu, l'aperçu de la destination et la navigation clavier native — que la
 * version bouton aurait fallu réécrire.
 *
 * # L'ordre des sections est le message, et il y en a deux
 *
 * Il n'y a pas un ordre parce qu'il n'y a pas un visiteur.
 *
 * **Sans bibliothèque** : « Commence ici », « Tout le répertoire », puis « Ma
 * bibliothèque » réduite à une ligne et à une promesse (« Avec un compte »).
 * Quelqu'un qui arrive n'a rien à reprendre et rien à ranger ; mettre sa
 * bibliothèque en tête reviendrait à ouvrir la maison sur une pièce vide.
 * C'est exactement l'argument que le code tenait déjà pour « Reprendre »,
 * étendu d'un cran.
 *
 * **Avec une bibliothèque** : « Reprendre », « Ma bibliothèque », puis les
 * sélections et le répertoire. Les sélections servent à trouver un *premier*
 * morceau, et on ne trouve un premier morceau qu'une fois : à quatorze entrées,
 * les laisser au-dessus ferait descendre chaque jour ce pour quoi on revient.
 *
 * La bascule se produit une fois, à la première entrée ajoutée, et plus jamais.
 * C'est le seul moment où le produit change de nature pour cette personne :
 * avant, elle cherche un morceau ; après, elle en a.
 *
 * # Les numéros suivent la position, pas la rubrique
 *
 * Ils numérotent l'ordre de lecture de la carte, donc ils se renumérotent avec
 * elle. Un `00` collé à « Commence ici » dans les deux ordres dirait que la
 * section est première alors qu'elle est troisième.
 *
 * # « Parcourir les N morceaux » reste en bas
 *
 * C'est la sortie de secours de qui sait déjà ce qu'il cherche, pas la porte
 * d'entrée : mise en tête, elle transforme un catalogue curé en base de données
 * à fouiller, et 221 lignes ne sont pas une réponse à « qu'est-ce que j'ai
 * envie de jouer ».
 *
 * Les sélections de « COMMENCE ICI » viennent de `/shelves` — mêmes clés, mêmes
 * titres que la grille de l'accueil. Les coder en dur ici, ce serait deux
 * listes à garder en phase, dont une invisible depuis le serveur.
 */

/**
 * Au-delà, la liste des playlists se replie sur une ligne.
 *
 * Six est le nombre de lignes que la section « Ma bibliothèque » peut porter
 * sans repousser « Commence ici » sous la ligne de flottaison d'un écran de
 * 700 px. Au-delà, une carte de navigation devient un sommaire, et un sommaire
 * ne se lit pas d'un coup d'œil.
 */
const VISIBLE_PLAYLISTS = 6;

type Props = {
  /** Les sélections, décidées par le serveur et reçues du layout. */
  selections: readonly CatalogShelf[];
  /** L'état d'URL courant, pour conserver `?drafts=1` d'un lien à l'autre. */
  state: LibraryBrowseState;
  /** Chemin courant, pour marquer la ligne active. */
  pathname: string;
  /** Fermeture demandée après une navigation, quand la sidebar est un tiroir. */
  onNavigate: () => void;
};

export function LibrarySidebar({
  selections,
  state,
  pathname,
  onNavigate,
}: Props) {
  const { counts, resumeTitle, playlists } = useLibraryOverview();
  const onHome = pathname === "/library";
  const hasLibrary = counts.all > 0;
  /*
    Masquée quand il n'y a rien à reprendre : une section « Reprendre » vide
    dirait à un nouvel arrivant qu'il a oublié quelque chose. Le compte lu est
    `practiced` et non `all` — avoir des morceaux n'est pas les avoir
    travaillés.
  */
  const canResume = counts.practiced > 0 && resumeTitle !== null;

  /*
    Les sections sont décrites, puis ordonnées, puis numérotées — dans cet
    ordre. Numéroter à la construction aurait figé les numéros dans l'ordre
    d'écriture du fichier, qui n'est pas celui de l'affichage : « Ma
    bibliothèque » serait sortie « 03 » en tête de carte.
  */
  const startHere =
    selections.length === 0 ? null : (
      <Section key="start" title="Commence ici">
        {selections.map((shelf) => (
          <Row
            key={shelf.key}
            href={librarySelectionPath(shelf.key, state.drafts)}
            label={shelf.title}
            active={pathname === librarySelectionPath(shelf.key, false)}
            onNavigate={onNavigate}
          />
        ))}
      </Section>
    );

  const browseAll = (
    <Section key="all" title="Tout le répertoire">
      <Row
        href={libraryBrowseAllPath(state.drafts)}
        label="Parcourir tous les morceaux"
        active={pathname === "/library" && state.sort === "title"}
        onNavigate={onNavigate}
      />
    </Section>
  );

  const mine = (
    <Section key="mine" title="Ma bibliothèque">
      <Row
        href={LIBRARY_MINE_PATH}
        label="Mes morceaux"
        hint={hasLibrary ? pieceCountHint(counts.all) : "Avec un compte"}
        active={pathname === LIBRARY_MINE_PATH}
        onNavigate={onNavigate}
      />

      {/* Masquée à zéro : personne ne t'a rien envoyé, la ligne n'a rien à dire. */}
      {counts.shared > 0 && (
        <Row
          href={LIBRARY_SHARED_PATH}
          label="Partagé avec moi"
          hint={pieceCountHint(counts.shared)}
          active={pathname === LIBRARY_SHARED_PATH}
          onNavigate={onNavigate}
        />
      )}

      {/*
        Les playlists vivent ici et nulle part ailleurs. Une page d'index
        existe, mais la carte du répertoire est le seul endroit d'où une
        playlist est atteignable en un clic depuis n'importe quelle page.
      */}
      {playlists.slice(0, VISIBLE_PLAYLISTS).map((playlist) => (
        <Row
          key={playlist.id}
          href={libraryPlaylistPath(playlist.id)}
          label={playlist.name}
          hint={pieceCountHint(playlist.pieceCount)}
          active={pathname === libraryPlaylistPath(playlist.id)}
          onNavigate={onNavigate}
        />
      ))}

      {playlists.length > 0 && (
        <Row
          href={LIBRARY_PLAYLISTS_PATH}
          label={
            playlists.length > VISIBLE_PLAYLISTS
              ? "Toutes mes playlists"
              : "Mes playlists"
          }
          active={pathname === LIBRARY_PLAYLISTS_PATH}
          onNavigate={onNavigate}
        />
      )}

      {/*
        Pas de playlist : la ligne d'index devient l'invitation à en créer une.
        Un bouton « Nouvelle playlist » dans la carte de navigation ouvrirait un
        dialogue par-dessus la page qu'on est en train de lire ; le lien mène à
        l'écran qui sait le faire, et qui explique au passage à quoi ça sert.
      */}
      {hasLibrary && playlists.length === 0 && (
        <Row
          href={LIBRARY_PLAYLISTS_PATH}
          label="Nouvelle playlist"
          active={pathname === LIBRARY_PLAYLISTS_PATH}
          onNavigate={onNavigate}
        />
      )}
    </Section>
  );

  const resume = !canResume ? null : (
    <Section key="resume" title="Reprendre">
      {/*
        La ligne mène à « Là où tu t'es arrêté » et non directement au morceau :
        un import privé n'a pas de fiche de catalogue, et `entry.openable` peut
        être faux. Envoyer sur une page qui sait traiter les deux cas coûte un
        clic de plus et zéro cul-de-sac.

        Son indice porte le titre du morceau, donc la ligne se lit sans être
        cliquée.
      */}
      <Row
        href={LIBRARY_RESUME_PATH}
        label="Là où tu t'es arrêté"
        hint={resumeTitle ?? undefined}
        active={pathname === LIBRARY_RESUME_PATH}
        onNavigate={onNavigate}
      />
    </Section>
  );

  const sections = (
    hasLibrary
      ? [resume, mine, startHere, browseAll]
      : [startHere, browseAll, mine]
  ).filter((section): section is ReactElement<SectionProps> => section !== null);

  return (
    <nav
      aria-label="Sélections de la bibliothèque"
      className="flex-1 min-h-0 flex flex-col overflow-y-auto"
    >
      <Link
        href={libraryHomePath(state.drafts)}
        onClick={onNavigate}
        className="block px-4 pt-4 pb-3.5 border-b border-border-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
        style={{
          background: onHome
            ? "linear-gradient(135deg, var(--color-violet-soft), transparent 70%)"
            : undefined,
        }}
        aria-current={onHome ? "page" : undefined}
      >
        <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-text-muted mb-1.5">
          Bibliothèque
        </span>
        <span
          className="block text-[18px] font-medium tracking-[-0.01em]"
          style={{
            color: onHome
              ? "var(--color-accent-bright)"
              : "var(--color-text-primary)",
          }}
        >
          Le répertoire
        </span>
      </Link>

      {sections.map((section, index) =>
        cloneElement(section, { tag: String(index).padStart(2, "0") }),
      )}

      {/*
        L'attribution du corpus, à l'endroit où le corpus est consulté.

        Les partitions sont individuellement en CC0 ou Public Domain Mark, qui
        n'exigent rien ; c'est le corpus qui les rassemble, PDMX, qui est en
        CC BY 4.0 et demande crédit. Le détail vit dans /mentions — ici il ne
        reste qu'une ligne, parce qu'une attribution est due, pas exposée.

        En pied de sidebar et non sous le canevas : l'obligation est réelle,
        mais elle n'a pas à manger en permanence une bande de la surface où
        l'on choisit un morceau.

        `mt-auto` la colle en bas quand la carte est courte, et la laisse
        suivre le flux quand elle déborde.
      */}
      <p className="mt-auto px-4 py-3 text-[11px] leading-[16px] text-text-muted border-t border-border-subtle">
        Répertoire du domaine public issu du corpus{" "}
        <Link
          href="/mentions"
          className="underline underline-offset-2 transition-colors duration-150 hover:text-text-secondary"
        >
          PDMX
        </Link>
        , sous licence CC BY 4.0
      </p>
    </nav>
  );
}

/** « 1 morceau », « 14 morceaux ». Jamais « 0 morceau » : la ligne est masquée. */
function pieceCountHint(count: number): string {
  return `${count} morceau${count > 1 ? "x" : ""}`;
}

/**
 * Le numéro est posé par le parent au moment où il ordonne ses sections, d'où
 * son absence ici : une section ne connaît pas son rang, elle le reçoit.
 */
type SectionProps = {
  title: string;
  tag?: string;
  children: ReactNode;
};

function Section({ title, tag, children }: SectionProps) {
  return (
    <section className="pt-3.5 pb-2.5 border-b border-border-subtle">
      <header className="flex items-baseline gap-2 px-4 pb-2">
        <span className="font-mono text-[9px] font-semibold tracking-[0.12em] text-accent-bright opacity-55">
          {tag}
        </span>
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-secondary">
          {title}
        </span>
      </header>
      {children}
    </section>
  );
}

function Row({
  href,
  label,
  hint,
  active,
  onNavigate,
}: {
  href: string;
  label: string;
  hint?: string;
  active: boolean;
  onNavigate: () => void;
}) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className="library-row block px-4 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
      data-active={active ? "" : undefined}
    >
      <span
        className="block text-[13px] leading-tight"
        style={{
          color: active
            ? "var(--color-accent-bright)"
            : "var(--color-text-primary)",
        }}
      >
        {label}
      </span>
      {hint !== undefined && (
        <span className="block font-mono text-[10px] tracking-[0.04em] text-text-muted mt-0.5 truncate">
          {hint}
        </span>
      )}
    </Link>
  );
}
